import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { formatFileSize } from '@/utils/mediaValidation';
import type { PortfolioStoragePool } from '@/types/package';

const BYTES_PER_MB = 1024 * 1024;
const size = (mb: number): string => formatFileSize(mb * BYTES_PER_MB);

/**
 * Brand Portfolio storage pool (portfolio staff): the shared pool's size, how much of it the brands use, and which
 * brands overflow into it. Each brand keeps the storage its Plan includes; only what exceeds it is drawn from here.
 */
export const StoragePoolCard = ({ pool }: { pool: PortfolioStoragePool }) => {
  const { t } = useTranslation('settings');
  const percentage = pool.poolMb > 0 ? Math.min((pool.poolUsedMb / pool.poolMb) * 100, 100) : 0;
  return (
    <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      <div className="p-5 sm:p-6 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-base font-semibold text-gray-900">{t('settings_storage_pool_title')}</h3>
          <Link to="/shop" className="text-sm font-medium text-primary-600 hover:text-primary-700">
            {t('settings_storage_pool_add')}
          </Link>
        </div>
        <p className="text-sm text-gray-600">{t('settings_storage_pool_hint')}</p>
        {pool.poolMb > 0 ? (
          <div>
            <p className="text-sm">
              <span className="font-medium">{size(pool.poolUsedMb)}</span>
              <span className="text-gray-500"> {t('settings_of')} {size(pool.poolMb)}</span>
            </p>
            <div className="mt-1.5 h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
              <div
                className={percentage >= 100 ? 'h-full rounded-full bg-red-500' : percentage >= 80 ? 'h-full rounded-full bg-amber-500' : 'h-full rounded-full bg-primary-500'}
                style={{ width: `${percentage}%` }}
              />
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-500">{t('settings_storage_pool_none')}</p>
        )}
        {pool.brands.length > 0 ? (
          <ul className="divide-y divide-gray-100">
            {pool.brands.map((brand) => (
              <li key={brand.teamId} className="flex items-center justify-between gap-3 py-2 text-sm">
                <span className="flex items-center gap-2 text-gray-900">
                  <Icon name={ICONS.CAUTION} size="sm" color="text-amber-600" ariaHidden />
                  {brand.name}
                  {!brand.isActive && <span className="text-gray-500">({t('settings_storage_pool_paused')})</span>}
                </span>
                <span className="text-gray-600">{t('settings_storage_pool_brand_over', { over: size(brand.overflowMb) })}</span>
              </li>
            ))}
          </ul>
        ) : (
          pool.poolMb > 0 && <p className="text-sm text-gray-500">{t('settings_storage_pool_no_overflow')}</p>
        )}
      </div>
    </div>
  );
};
